import { LitElement, html, css } from 'lit';
import { phoenixCustomElement } from '../../lib/dependencies.js';
import { until } from 'lit/directives/until.js';
import { repeat } from 'lit/directives/repeat.js';

import './partials/paymentPlan';

/**
 * PhoenixMembershipSignup
 *
 * This web component will display product offers similarly to the WebChilliwack component 'MembershipSignup'.
 * However it is possible to drop/import it in any HTML file which allows its use in Martketing Landing Page
 * third party platform such as Instapage.
 *
 * @example
 *  <phoenix-membership-signup config='${JSON.stringify(config)}'>
    </phoenix-membership-signup>
 */

export default class PhoenixMembershipSignup extends LitElement {
    static properties = {
        config: {
            type: Object,
        },
    };

    static get styles() {
        return css`
            :host,
            :host * {
                font-family: sans-serif;
                box-sizing: border-box;
            }
            .membership-signup {
                padding: 0 24px 36px 24px;
                background-color: #f8f8f8;
            }
            .membership-signup-action-title {
                display: flex;
                justify-content: center;
                padding: 32px;
                font-weight: 700;
                font-size: 1.25rem;
            }
            .plans-wrapper {
                display: flex;
                flex-direction: row;
                flex-flow: row wrap;
                gap: 10px;
                justify-content: center;
                align-items: stretch;
                margin-bottom: 44px;
            }
            .plan-outter-wrapper {
                margin-right: 10px;
            }
            .plan-wrapper {
                padding: 24px 23px 18px 23px;
                width: 200px;
                min-height: 300px;
                text-align: center;
                grid-template-areas: 'ribbon' 'interval' 'unitAmount' 'marketingPoints' 'amountPerYear';
                cursor: pointer;
                display: grid;
                overflow: hidden;
                position: relative;
                box-sizing: border-box;
                align-items: center;
            }
            .plan-card {
                display: flex;
            }
            .payment-plan + label {
                border-radius: 16px;
                border: 3px solid #dbdee7;
                display: block;
                background-color: #fff;
            }
            .payment-plan:checked + label {
                border-color: #0054a1;
                box-shadow: 0px 0px 18px rgba(0, 0, 0, 0.18), 0px 0px 32px rgba(0, 0, 0, 0.08);
            }
            .payment-plan {
                display: none;
            }
            .payment-plan:checked + label .plan-amount-button {
                color: #fff;
                background-color: #000;
                font-weight: bold;
            }
            .email-capture {
                margin: 0 auto;
                max-width: 582px;
                text-align: center;
            }
            .email-capture input {
                padding: 18.5px 14px;
                font: inherit;
                color: currentColor;
                width: 95%;
                border: 1px solid #dbdee7;
                border-radius: 6px;
                height: 1.1876em;
                margin: 0;
                display: block;
            }
            .email-capture button {
                border: none;
                border-radius: 6px;
                color: rgba(0, 0, 0, 0.26);
                box-shadow: none;
                background-color: rgba(0, 0, 0, 0.12);
                padding: 8px 22px;
                font-size: 0.9375rem;
            }
            .email-capture button:enabled {
                cursor: pointer;
                color: #fff;
                background-color: #000;
                font-weight: bold;
            }
            .privacy {
                text-align: center;
                margin: 12px 0px 24px 0px;
                padding: 0px 8px;
            }
            .banner {
                color: #fff;
                grid-area: ribbon;
                box-sizing: content-box;
                font-weight: bold;
                background-color: #e51937;
                left: -23px;
                position: relative;
                font-size: 0.625rem;
                line-height: 1.25rem;
            }
            .banner::after {
                content: '';
                top: 0;
                right: -17px;
                border: 1em solid #e51937;
                display: block;
                position: absolute;
                border-left-width: 10px;
                border-right-color: transparent;
            }
            .banner .text {
                overflow: hidden;
                white-space: nowrap;
            }
            .no-banner {
                height: 1.25rem;
            }
            .interval {
                font-size: 1rem;
                grid-area: interval;
                font-weight: 500;
                line-height: 1.25rem;
                margin-top: 10px;
            }
            .plan-amount {
                grid-area: unitAmount;
            }
            .plan-amount .amount {
                font-size: 1.875rem;
                font-weight: bold;
                line-height: 2.5rem;
            }
            .plan-amount-button {
                cursor: pointer;
                grid-area: amountPerYear;
                margin-top: 8px;
                border: 1px solid rgba(0, 0, 0, 0.23);
                padding: 5px 15px;
                font-weight: bold;
                text-transform: uppercase;

                width: 75%;
                margin-right: auto;
                margin-left: auto;
                margin-top: 10px;
                color: rgba(0, 0, 0, 1);
                padding: 6px 16px;
                font-size: 0.875rem;
                min-width: 64px;
                box-sizing: border-box;
                transition: background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,
                    box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,
                    border 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
                font-family: IBM Plex Sans, Helvetica Neue, Arial, sans-serif;
                font-weight: 500;
                line-height: 1.75;
                border-radius: 6px;
                text-transform: none;
            }

            .plan-amount-button :hover {
                opacity: 0.6;
            }
            .marketing-points {
                font-size: 0.875rem;
                grid-area: marketingPoints;
                font-weight: 500;
                line-height: 1.25rem;
            }
            .advantage {
                color: #098600;
            }
            .billing {
                opacity: 0.72;
                padding: 4px 0;
            }
            .description {
                opacity: 0.5;
            }
            .more-text {
                display: none;
            }
            .text.show-more .more-text {
                display: inline;
            }
            .view-more {
                cursor: pointer;
            }
        `;
    }

    constructor() {
        super();
        this.config = {};
    }

    connectedCallback() {
        super.connectedCallback();
        const currentLocation = this.config.locationUrl ? new URL(this.config.locationUrl) : window?.location;
        this.urlParams = new URLSearchParams(currentLocation?.search);
        this.targetUrl = this.urlParams.get('targeturl') || '';
        this.urlParams.delete('targeturl');
    }

    _getMarketingCopy() {
        if (this?.config?.marketingCopy?.length) {
            return this?.config?.marketingCopy || [];
        }

        const getMarketingCopyRequest = {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
            mode: 'no-cors',
        };

        if (this?.config?.signupUrl) {
            const signupUrlArray = this.config.signupUrl.split('signup');
            signupUrlArray.splice(1, 0, '.api/item/signup');
            const itemUrl = signupUrlArray.join('');
            return fetch(itemUrl, getMarketingCopyRequest)
                .then((response) => {
                    return response.text();
                })
                .then((result) => {
                    return result?.extendedProperties?.subscription_plans ?? [];
                })
                .catch((error) =>
                    // eslint-disable-next-line no-console
                    console.error(error)
                );
        } else {
            return [];
        }
    }

    _getPlans() {
        const getPlansRequest = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                codes: this.config.plans,
                siteKeyword: this.config.siteKeyword,
            }),
        };

        if (this?.config?.plansUrl) {
            return fetch(this.config.plansUrl, getPlansRequest)
                .then((response) => response.json())
                .then((data) => {
                    return data.plans;
                })
                .catch((error) =>
                    // eslint-disable-next-line no-console
                    console.error(error)
                );
        } else {
            return [];
        }
    }

    _getData() {
        return Promise.all([this._getPlans(), this._getMarketingCopy()]).then((data) => {
            const plans = data[0];
            const marketingCopies = data[1];
            return plans.map((plan) => {
                const marketingCopy = marketingCopies.find((x) => x.planCode === plan.code) || {};
                return {
                    marketingCopy,
                    ...plan,
                };
            });
        });
    }

    _getPlanVariants() {
        return {
            ft: 'Free Trial',
            sp: 'Straight Paid',
        };
    }

    _getBrandByPlanCode(planCode) {
        const brandInitials = planCode.split('-')[0];
        return brandInitials.toUpperCase();
    }

    _getVariantByPlanCode(planCode) {
        const planCodeSplit = planCode.split('-');
        const planVariants = this._getPlanVariants();
        return planVariants[planCodeSplit[4]];
    }

    _getTermByPlan(plan) {
        return `${plan.intervalLength} ${plan.intervalUnit}`;
    }

    _pushToDataLayer(data) {
        if (typeof window !== 'undefined') {
            const dataLayer = window.dataLayer || [];
            window.dataLayer = dataLayer;
            dataLayer.push(data);
        }
    }

    _sendProductImpressionToDL = (plans) => {
        if (plans?.length) {
            const products = plans.map((p) => ({
                brand: this._getBrandByPlanCode(p.code),
                category: this._getTermByPlan(p),
                id: p.code,
                name: p.name,
                price: p.unitAmount.toFixed(2),
                variant: this._getVariantByPlanCode(p.code),
                list: p?.product?.description,
            }));
            this._pushToDataLayer({
                event: 'product-impression',
                ecommerce: {
                    currencyCode: 'USD',
                    impressions: products,
                },
            });
        }
    };

    _sendProductClickToDL = (selectedPlan, index) => {
        if (selectedPlan) {
            this._pushToDataLayer({
                event: 'product-click',
                ecommerce: {
                    currencyCode: 'USD',
                    click: {
                        actionField: {
                            list: selectedPlan?.product?.description,
                        },
                        products: [
                            {
                                name: selectedPlan?.name,
                                id: selectedPlan?.code,
                                brand: this._getBrandByPlanCode(selectedPlan?.code || ''),
                                category: this._getTermByPlan(selectedPlan),
                                variant: this._getVariantByPlanCode(selectedPlan?.code || ''),
                                price: selectedPlan?.unitAmount.toFixed(2),
                                position: index,
                            },
                        ],
                    },
                },
            });
        }
    };

    render() {
        const { fallbackUrl, source, siteKeyword } = this.config || {};
        const baseUrl = `https://www.${siteKeyword === 'thestreet' ? 'thestreet' : 'si'}.com/signup/`;
        const redirectUrl = this.targetUrl ? `${baseUrl}${this.targetUrl}` : fallbackUrl;
        return html`<div class="membership-signup">
            <div class="membership-signup-action-title">Choose Your Plan</div>
            <form method="GET" action="${redirectUrl}">
                ${this._renderPaymentPlans()} ${this._renderEmailCapture()}
                <input type="hidden" id="source" name="source" value="${source}" />
                <input type="hidden" id="siteKeyword" name="siteKeyword" value="${siteKeyword}" />
                ${repeat(
                    this.urlParams,
                    (param, index) => `${param}${index}`,
                    (param) => html`<input type="hidden" name="${param[0]}" value="${param[1]}" />`
                )}
            </form>
        </div>`;
    }

    _renderPlans(plans) {
        const defaultSelectedIndex = plans.length > 1 ? this.config?.defaultSelectedIndex ?? 0 : 0;
        this._sendProductImpressionToDL(plans);
        return html`${repeat(
            plans,
            (plan, index) => plan.code || index,
            (plan, index) => html`<phoenix-membership-signup-plan
                class="plan-card"
                config=${JSON.stringify({
                    ...plan,
                    isSelected: index === defaultSelectedIndex,
                    position: index,
                })}
                .sendProductClickToDL=${this._sendProductClickToDL}
            ></phoenix-membership-signup-plan>`
        )}`;
    }

    _renderPaymentPlans() {
        return html`<div class="plans-wrapper">
            ${until(
                this._getData().then((resp) => this._renderPlans(resp)),
                html`<p>Loading...</p>`
            )}
        </div>`;
    }

    _renderEmailCapture() {
        return html`<div class="email-capture">
            <input name="email" placeholder="Enter your email" type="email" required="true" />
            <div class="privacy">
                We handle your personal information in accordance with our
                <a href="https://thearenagroup.net/privacy-policy/" target="_blank" rel="noreferrer">
                    Privacy Policy.</a
                >
            </div>
            <button type="submit">CONTINUE</button>
        </div>`;
    }
}

phoenixCustomElement('phoenix-membership-signup', PhoenixMembershipSignup);
